import classNames from "classnames";
import noop from "lodash/noop";
import { MaterialTableColumn, MaterialTableProps, TableWrapperProps } from "./types";
import NoDataFound from "../noDataFound";
import { TableVirtuoso } from "react-virtuoso";
import React from "react";
import { MATERIAL_TABLE_INCREASE_VIEWPORT_BOTTOM, MATERIAL_TABLE_INCREASE_VIEWPORT_TOP } from "shared/utils/constants";
/**
 * Use this table to show data in plain html table.
 *
 *
 * @param data = Array[dataItem]
 * @param columns = [
            {
              className: "name", //optional
              name: "Name",
              Cell: dataItem => dataItem.name
            },
            {
              className: "description",
              name: "Description",
              Cell: dataItem => dataItem.Description
            }
          ];
 */

function TableWrapper({ whiteBg, hideShadow, children, ...props }: TableWrapperProps) {
  return (
    <table
      {...props}
      className={classNames("materialTable", {
        whiteBg: whiteBg,
        hideShadow: hideShadow
      })}>
      {children}
    </table>
  );
}

function TableHeaderRowContent<T>(props: { columns: Array<MaterialTableColumn<T>> }) {
  return (
    <tr>
      {props.columns.map((column, index) => (
        <th key={index} className={column.className} style={{ width: `${column.width}%` }}>
          {column.name}
        </th>
      ))}
    </tr>
  );
}

function TableDataRowContent<T>(props: { rowIndex: number; item: T; columns: Array<MaterialTableColumn<T>> }) {
  return (
    <React.Fragment>
      {props.columns.map((column, colIndex) => (
        <td key={colIndex} className={column.className} style={{ width: `${column.width}%` }}>
          {column.Cell(props.item, props.rowIndex)}
        </td>
      ))}
    </React.Fragment>
  );
}

function TableNoResultComponent(props: { showInlineNoResult?: boolean }) {
  return <NoDataFound inline={props.showInlineNoResult} />;
}

function MaterialTableBasic<T>({ onRowClick = noop, ...props }: MaterialTableProps<T>) {
  return (
    <TableWrapper whiteBg={props.whiteBg} hideShadow={props.hideShadow}>
      <thead>
        <TableHeaderRowContent columns={props.columns} />
      </thead>
      <tbody>
        {props.data.map((item, rowIndex) => (
          <tr key={rowIndex} onClick={() => onRowClick(item, rowIndex)}>
            <TableDataRowContent rowIndex={rowIndex} item={item} columns={props.columns} />
          </tr>
        ))}
        {props.data.length === 0 && (
          <tr>
            <td colSpan={props.columns.length}>
              <TableNoResultComponent showInlineNoResult={props.showInlineNoResult} />
            </td>
          </tr>
        )}
      </tbody>
    </TableWrapper>
  );
}

function MaterialTableVirtualized<T>({ onRowClick = noop, ...props }: MaterialTableProps<T>) {
  return (
    <TableVirtuoso
      components={{
        Table: tableProps => <TableWrapper whiteBg={props.whiteBg} children={tableProps.children} />,
        EmptyPlaceholder: () => <TableNoResultComponent showInlineNoResult={props.showInlineNoResult} />
      }}
      fixedHeaderContent={() => <TableHeaderRowContent columns={props.columns} />}
      data={props.data}
      itemContent={(rowIndex, item) => <TableDataRowContent rowIndex={rowIndex} item={item} columns={props.columns} />}
      endReached={props.endReachedHandler}
      increaseViewportBy={{
        top: MATERIAL_TABLE_INCREASE_VIEWPORT_TOP,
        bottom: MATERIAL_TABLE_INCREASE_VIEWPORT_BOTTOM
      }}
    />
  );
}

function MaterialTable<T>({ onRowClick = noop, ...props }: MaterialTableProps<T>) {
  return (
    <React.Fragment>
      {props.isVirtualized ? (
        <MaterialTableVirtualized {...props} onRowClick={onRowClick} />
      ) : (
        <MaterialTableBasic {...props} onRowClick={onRowClick} />
      )}
    </React.Fragment>
  );
}

export default MaterialTable;
